import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { memo, type ReactElement, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FreshnessIndicator } from '../../components/FreshnessIndicator';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';
import { HistoryTab } from '../history';
import { type FreshnessInput, useFreshness } from '../shared/use-freshness';

import { PositionCard } from './PositionCard';
import { type PositionsView, toPositionsView } from './positions-model';
import { type PositionsQuery, usePositions } from './use-positions';

/** The two tabs of the Positions screen, in order: E8 (M5) and E9 (M6). */
const TABS = ['open', 'history'] as const;
/** A tab of the Positions screen: `open` is E8, `history` is E9. */
type PositionsTab = (typeof TABS)[number];

/** Mockup 7 and Historique: segmented control, 4px inset, 40px tabs, 9px corners. */
const SEGMENTED = {
  radius: '12px',
  tabRadius: '9px',
  tabHeight: 40,
  fontSize: 14,
} as const;

/**
 * Screen Positions: E8 "Open" (specs/M5-positions/spec.md; US-15, US-17; mockup 7) and E9
 * "History" (specs/M6-history/spec.md; US-16; mockup Historique) as two tabs. `GET /positions` is
 * refreshed every 5 s while the app is visible and the "Open" tab is shown (M5 R15, M7 R1–R4).
 */
export function PositionsScreen(): ReactElement {
  // The one source of the selected tab: it decides what is shown and whether E8 is polled (E9 on
  // display counts as leaving E8, M7 R4).
  const [tab, setTab] = useState<PositionsTab>('open');
  const query = usePositions({ poll: tab === 'open' });
  return <PositionsContent query={query} tab={tab} onTabChange={setTab} />;
}

/**
 * What the Positions screen shows for a given query state and tab: the title, the two tabs, then
 * the open tab (first-load failure with Retry, skeletons, freshness line or failed-refresh banner,
 * cards or empty state, manual count) or the History tab. Split from {@link PositionsScreen} so any
 * query state can be rendered directly in tests. The tab is controlled by the screen, which also
 * stops polling E8 while History is shown. The History tab is mounted only while selected: opening
 * it fetches the history (M6 R9).
 */
export function PositionsContent({
  query,
  tab,
  onTabChange,
}: {
  readonly query: PositionsQuery;
  /** The tab on display. */
  readonly tab: PositionsTab;
  /** Called with the tab the trader selects. */
  readonly onTabChange: (tab: PositionsTab) => void;
}): ReactElement {
  const { t } = useTranslation();
  const ids = { open: useId(), history: useId(), panel: useId() };
  // Recomputed only when a new list arrives, not when `refreshing` flips (twice per 5 s refresh).
  const view = useMemo(
    () => (query.data === undefined ? undefined : toPositionsView(query.data.positions)),
    [query.data],
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pb: 1 }}>
      <Typography variant="h5" component="h1" sx={{ mx: 0.5 }}>
        {t('positions.title')}
      </Typography>
      <Tabs
        value={tab}
        onChange={(_event, value: PositionsTab) => {
          onTabChange(value);
        }}
        aria-label={t('positions.tabs.label')}
        variant="fullWidth"
        slotProps={{ indicator: { sx: { display: 'none' } } }}
        sx={(theme) => ({
          minHeight: 0,
          p: 0.5,
          bgcolor: theme.fx.neutralBg,
          borderRadius: SEGMENTED.radius,
        })}
      >
        {TABS.map((value) => (
          <Tab
            key={value}
            value={value}
            id={ids[value]}
            aria-controls={tab === value ? ids.panel : undefined}
            label={
              value === 'history'
                ? t('positions.tabs.history')
                : view === undefined
                  ? t('positions.tabs.open')
                  : t('positions.open', { count: view.fxmate.length })
            }
            sx={(theme) => ({
              minHeight: SEGMENTED.tabHeight,
              borderRadius: SEGMENTED.tabRadius,
              fontSize: SEGMENTED.fontSize,
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
              '&.Mui-selected': { bgcolor: theme.fx.surface, color: 'text.primary' },
            })}
          />
        ))}
      </Tabs>
      <Box
        role="tabpanel"
        id={ids.panel}
        aria-labelledby={ids[tab]}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}
      >
        {tab === 'history' ? <HistoryTab /> : <OpenTab query={query} view={view} />}
      </Box>
    </Box>
  );
}

/**
 * The "Open" tab (E8, M5 §4): a first load that failed shows its reason with Retry (M5 §4
 * "Error"); otherwise skeletons, then how current the list is (M7 §4) and the cards or empty
 * state, and the manual count. A failed 5 s refresh keeps the cards under the M7 banner (Offline
 * or the refusal's reason), retried at the next tick, never with the M5 Retry alert: M5 §4 "Error"
 * read with M7 R7 and §4, as Home does (proposal, to be validated; M5 and M7 Changes, 14/10).
 */
function OpenTab({
  query,
  view,
}: {
  readonly query: PositionsQuery;
  readonly view: PositionsView | undefined;
}): ReactElement {
  return (
    <>
      {query.error !== undefined && (
        <LoadFailed
          reasonCode={query.error.reasonCode}
          params={query.error.params}
          onRetry={query.refetch}
          retrying={query.retrying}
        />
      )}
      {/* `view` alone is enough at run time (it exists exactly when `data` does); the two other
          checks narrow `data` and `receivedAt` for TypeScript, without a non-null assertion. */}
      {view === undefined || query.data === undefined || query.receivedAt === undefined ? (
        query.error === undefined && <Loading />
      ) : (
        <>
          <Freshness
            asOf={query.data.refreshedAt}
            receivedAt={query.receivedAt}
            refreshing={query.refreshing}
            refreshError={query.refreshError}
          />
          <OpenList view={view} receivedAt={query.receivedAt} />
        </>
      )}
    </>
  );
}

/**
 * The cards or the empty state, then the manual count. Memoised as Home's body is: the
 * `refreshing` flag, which flips twice per 5 s refresh, re-renders only the freshness line.
 */
const OpenList = memo(function OpenList({
  view,
  receivedAt,
}: {
  readonly view: PositionsView;
  readonly receivedAt: number;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      {view.fxmate.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('positions.empty')}
          </Typography>
        </Paper>
      ) : (
        view.fxmate.map((position) => (
          <PositionCard key={position.id} position={position} now={receivedAt} />
        ))
      )}
      {/* FXM-04 §4: manual positions are counted, never listed nor actionable (R4, Q55). */}
      {view.manualCount > 0 && (
        <Typography variant="body2" sx={{ mx: 0.5, color: 'text.secondary', lineHeight: 1.5 }}>
          {t('positions.manual', { count: view.manualCount })}
        </Typography>
      )}
    </>
  );
});

/**
 * How current the list is (M7 §4): the discreet line above the cards, or the failed-refresh
 * banner in its place. No mockup draws it (M7 §4): placed as on Home, under the tabs (proposal).
 */
function Freshness(props: FreshnessInput): ReactElement {
  return <FreshnessIndicator {...useFreshness(props)} />;
}

/** Height in px of a collapsed card of mockup 7 (one line: side, instrument, lots, channel). */
const CARD_SKELETON_HEIGHT = 68;

/** Skeleton cards while the first load is pending (spec §4 "Loading"), announced to screen readers. */
function Loading(): ReactElement {
  const { t } = useTranslation();
  return (
    <Box role="status" aria-label={t('positions.loading')} sx={{ display: 'grid', gap: 1.75 }}>
      <Skeleton variant="rounded" height={CARD_SKELETON_HEIGHT} />
      <Skeleton variant="rounded" height={CARD_SKELETON_HEIGHT} />
    </Box>
  );
}

/** Failure of `GET /positions`: only the translated reason, never a raw code or message (I9, I12). */
function LoadFailed({
  reasonCode,
  params,
  onRetry,
  retrying,
}: {
  readonly reasonCode: string;
  readonly params: ApiError['params'];
  readonly onRetry: () => void;
  readonly retrying: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const message = reasonMessage(reasonCode, params);
  return (
    <Alert
      severity="error"
      sx={(theme) => ({ bgcolor: theme.fx.dangerBg, color: theme.fx.text })}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={onRetry}
          loading={retrying}
          sx={{ minHeight: 36 }}
        >
          {t('positions.retry')}
        </Button>
      }
    >
      {t(message.key, message.values)}
    </Alert>
  );
}
